import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { listSchemas } from '../../api/schemas';
import { listRules } from '../../api/rules';
import { listIngestionSources } from '../../api/ingestionSources';
import { describeStore } from '../../api/stores';
import { getCredentials } from '../../api/client';
import { DataFlowGraph, DataFlowRule, DataFlowGraphHandle } from '../../components/DataFlowGraph';
import { GraphInfoPanel } from '../../components/GraphInfoPanel';
import { RegisterSchemaForm } from '../forms/RegisterSchemaForm';
import { AddRuleForm } from '../forms/AddRuleForm';

export interface SelectedEntity {
  type: 'schema' | 'rule' | 'ingress' | 'egress' | 'projection' | 'resource';
  id: string;
}

interface FlowTabProps {
  /** Single store name (store mode) */
  storeName: string;
  /** Multiple source stores (view mode) — fetches and merges all stores */
  sources?: string[];
  /** Pre-loaded schemas (skips API call) */
  preloadedSchemas?: string[];
  /** Pre-loaded rules (skips API call) */
  preloadedRules?: DataFlowRule[];
  /** Entity selected via URL params (from parent StoreDetail) */
  selectedEntity?: SelectedEntity | null;
  /** Callback when entity selection changes (URL push for schema/rule) */
  onEntitySelect?: (entity: SelectedEntity | null) => void;
}

function entityToNodeId(entity: SelectedEntity): string {
  switch (entity.type) {
    case 'schema': return `ev:${entity.id}`;
    case 'rule': return `cmp:${entity.id}`;
    case 'ingress': return `in:${entity.id}`;
    case 'egress': return `out:${entity.id}`;
    case 'projection': return `proj:${entity.id}`;
    case 'resource': return `cmp:${entity.id}`;
    default: return entity.id;
  }
}

function nodeIdToEntity(nodeId: string): { type: 'schemas' | 'rules'; id: string } | null {
  if (nodeId.startsWith('ev:')) return { type: 'schemas', id: nodeId.slice(3) };
  if (nodeId.startsWith('cmp:')) return { type: 'rules', id: nodeId.slice(4) };
  return null;
}

export function FlowTab({ storeName, sources, preloadedSchemas, preloadedRules, selectedEntity, onEntitySelect }: FlowTabProps) {
  const navigate = useNavigate();
  const graphHandleRef = useRef<DataFlowGraphHandle | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSchemaForm, setShowSchemaForm] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);

  const handleGraphHandle = useCallback((handle: DataFlowGraphHandle) => {
    graphHandleRef.current = handle;
  }, []);

  // Sync selected entity from URL params to local state + center graph
  useEffect(() => {
    if (selectedEntity) {
      const nodeId = entityToNodeId(selectedEntity);
      setSelectedId(nodeId);
      graphHandleRef.current?.centerOnNode(nodeId);
    } else {
      setSelectedId(null);
    }
  }, [selectedEntity]);

  // Track viewport width so the graph + info-panel grid can stack on narrow
  // screens instead of squeezing the graph into a tiny box on mobile.
  const [isNarrow, setIsNarrow] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Determine the set of stores in scope for this graph
  const storesInScope = useMemo(() => sources || [storeName], [sources, storeName]);
  const isViewMode = (sources?.length ?? 0) > 1;

  // --- Single-store queries (store mode, backwards-compatible) ---
  const { data: schemasData, isLoading: schemasLoading } = useQuery({
    queryKey: ['schemas', storeName],
    queryFn: () => listSchemas(storeName),
    enabled: !isViewMode && !preloadedSchemas && !!getCredentials(),
  });

  const { data: rulesData, isLoading: rulesLoading } = useQuery({
    queryKey: ['rules', storeName],
    queryFn: () => listRules(storeName),
    enabled: !isViewMode && !preloadedRules && !!getCredentials(),
  });

  const { data: storeData } = useQuery({
    queryKey: ['store', storeName],
    queryFn: () => describeStore(storeName),
    enabled: !isViewMode && !!getCredentials(),
  });

  const { data: ingestionData } = useQuery({
    queryKey: ['ingestion-sources', storeName],
    queryFn: () => listIngestionSources(storeName),
    enabled: !isViewMode && !!getCredentials(),
  });

  // --- Multi-store queries (view mode) ---
  const multiSchemaQueries = useQueries({
    queries: (isViewMode ? storesInScope : []).map(store => ({
      queryKey: ['schemas', store],
      queryFn: () => listSchemas(store),
      enabled: !!getCredentials(),
    })),
  });

  const multiRulesQueries = useQueries({
    queries: (isViewMode ? storesInScope : []).map(store => ({
      queryKey: ['rules', store],
      queryFn: () => listRules(store),
      enabled: !!getCredentials(),
    })),
  });

  const multiIngestionQueries = useQueries({
    queries: (isViewMode ? storesInScope : []).map(store => ({
      queryKey: ['ingestion-sources', store],
      queryFn: () => listIngestionSources(store),
      enabled: !!getCredentials(),
    })),
  });

  const multiLoading = isViewMode && (
    multiSchemaQueries.some(q => q.isLoading)
    || multiRulesQueries.some(q => q.isLoading)
    || multiIngestionQueries.some(q => q.isLoading)
  );

  // --- Merge and reclassify data ---
  const { schemas, rules, ingress, projectionConfig } = useMemo(() => {
    if (preloadedSchemas && preloadedRules) {
      return { schemas: preloadedSchemas, rules: preloadedRules, ingress: undefined, projectionConfig: undefined };
    }

    if (!isViewMode) {
      // Single-store mode — original behavior
      const schemas = ((schemasData?.data as any)?.schemas || []).map((s: any) => s.eventType);
      const raw = (rulesData?.data as any)?.rules || [];
      const rules: DataFlowRule[] = raw.map((r: any): DataFlowRule => ({
        name: r.name,
        ruleId: r.ruleId,
        sourceEventType: r.predicate?.eventType || r.sourceEventType || '*',
        target: r.action?.target || r.target || r.output || {},
      })).filter((r: DataFlowRule) => r.target && Object.keys(r.target).length > 0);

      const sources = (ingestionData?.data as any)?.sources || [];
      const ingressEntries = sources
        .filter((s: any) => s.status === 'ACTIVE')
        .map((s: any) => ({
          type: s.source?.type === 'EVENTSTORE' ? 'EventStore' : (s.source?.type || 'API'),
          name: s.source?.storeName || s.name,
          targetEventTypes: (s.events || []).map((e: any) => e.eventType).filter((t: string) => t && t !== '*'),
        }));

      const config = (storeData?.data as any)?.config || {};
      const projections = config.projections || config.projection;
      let projectionConfig;
      if (projections) {
        const proj = Array.isArray(projections) ? projections[0] : projections;
        projectionConfig = proj ? { reducerType: proj.accumulate?.operation || proj.reducer || 'MERGE_ALL', name: proj.name || 'projection' } : undefined;
      }

      return {
        schemas,
        rules,
        ingress: ingressEntries.length > 0 ? ingressEntries : undefined,
        projectionConfig,
      };
    }

    // --- View mode: merge all stores, reclassify cross-store routing ---
    const scopeSet = new Set(storesInScope);

    // Collect all schemas from all stores (deduplicate by eventType)
    const allSchemas = new Set<string>();
    multiSchemaQueries.forEach(q => {
      ((q.data?.data as any)?.schemas || []).forEach((s: any) => {
        if (s.eventType) allSchemas.add(s.eventType);
      });
    });

    // Collect all rules from all stores
    const allRules: DataFlowRule[] = [];
    const internalRoutes: { source: string; target: string }[] = [];

    multiRulesQueries.forEach((q, i) => {
      const store = storesInScope[i];
      const raw = (q.data?.data as any)?.rules || [];
      raw.forEach((r: any) => {
        const sourceEventType = r.predicate?.eventType || r.sourceEventType || '*';
        const target = r.action?.target || r.target || r.output || {};
        if (!target || Object.keys(target).length === 0) return;

        const targetType = (target.type || '').toUpperCase();
        const targetStore = target.storeName;

        if (targetType === 'EVENTSTORE' && targetStore && scopeSet.has(targetStore)) {
          // Cross-store routing WITHIN the view → internal flow, not egress.
          // Find what event types the target store accepts from this source.
          // For now, connect source event to the first schema of the target store.
          internalRoutes.push({ source: sourceEventType, target: targetStore });
        } else {
          // External target (outside view scope) → keep as egress
          allRules.push({
            name: `${store}:${r.name || r.ruleId}`,
            ruleId: r.ruleId,
            sourceEventType,
            target,
          });
        }
      });
    });

    // Collect ingestion sources — only show ingress from OUTSIDE the view scope
    const ingressEntries: { type: string; name: string; targetEventTypes: string[] }[] = [];
    multiIngestionQueries.forEach((q, i) => {
      const sources = (q.data?.data as any)?.sources || [];
      sources.filter((s: any) => s.status === 'ACTIVE').forEach((s: any) => {
        const sourceStore = s.source?.storeName;
        // Skip ingestion from stores within the view — that's internal routing
        if (sourceStore && scopeSet.has(sourceStore)) return;

        ingressEntries.push({
          type: s.source?.type === 'EVENTSTORE' ? 'EventStore' : (s.source?.type || 'API'),
          name: sourceStore || s.name,
          targetEventTypes: (s.events || []).map((e: any) => e.eventType).filter((t: string) => t && t !== '*'),
        });
      });
    });

    // Convert internal routes to synthetic "rules" that connect event→event directly.
    // Find which event types the target store ingests from the source.
    // Since we don't have per-ingestion-source event targeting at this level,
    // look at the target store's ingestion config for the source store.
    multiIngestionQueries.forEach((q, i) => {
      const targetStore = storesInScope[i];
      const sources = (q.data?.data as any)?.sources || [];
      sources.filter((s: any) => s.status === 'ACTIVE').forEach((s: any) => {
        const sourceStore = s.source?.storeName;
        if (!sourceStore || !scopeSet.has(sourceStore)) return;
        // This is an internal ingestion — find which events flow from sourceStore to targetStore
        const targetEventTypes = (s.events || []).map((e: any) => e.eventType).filter((t: string) => t && t !== '*');
        // Find rules from sourceStore that target this store
        const matchingRoutes = internalRoutes.filter(r => r.target === targetStore);
        matchingRoutes.forEach(route => {
          // The source event type triggers forwarding to target store's event types
          if (targetEventTypes.length > 0) {
            targetEventTypes.forEach((targetEt: string) => {
              allRules.push({
                name: `internal:${sourceStore}→${targetStore}:${route.source}→${targetEt}`,
                sourceEventType: route.source,
                target: { type: 'INTERNAL', storeName: targetStore, targetEventType: targetEt },
              });
            });
          }
        });
      });
    });

    return {
      schemas: Array.from(allSchemas),
      rules: allRules,
      ingress: ingressEntries.length > 0 ? ingressEntries : undefined,
      projectionConfig: undefined,
    };
  }, [isViewMode, storesInScope, schemasData, rulesData, storeData, ingestionData, multiSchemaQueries, multiRulesQueries, multiIngestionQueries, preloadedSchemas, preloadedRules]);

  const schemaItems = useMemo(() => {
    if (preloadedSchemas) return preloadedSchemas.map(s => ({ eventType: s, version: 1, status: 'ACTIVE' }));
    return ((schemasData?.data as any)?.schemas || []) as { eventType: string; version: number; status: string }[];
  }, [preloadedSchemas, schemasData]);

  const handleNodeClick = (nodeId: string) => {
    setSelectedId(nodeId);
    const entity = nodeIdToEntity(nodeId);
    if (entity && onEntitySelect) {
      onEntitySelect({ type: entity.type === 'schemas' ? 'schema' : 'rule', id: entity.id });
    }
  };

  const handlePanelClose = () => {
    setSelectedId(null);
    if (onEntitySelect) onEntitySelect(null);
  };

  const handlePanelNavigate = (path: string) => {
    navigate(path);
  };

  const handlePanelSelectNode = (nodeId: string) => {
    setSelectedId(nodeId);
    setTimeout(() => { graphHandleRef.current?.centerOnNode(nodeId); }, 50);
    const entity = nodeIdToEntity(nodeId);
    if (entity && onEntitySelect) {
      onEntitySelect({ type: entity.type === 'schemas' ? 'schema' : 'rule', id: entity.id });
    }
  };

  let content: React.ReactNode;

  if (!preloadedSchemas && !isViewMode && (schemasLoading || rulesLoading)) {
    content = <Box textAlign="center" padding="l"><Spinner size="large" /></Box>;
  } else if (multiLoading) {
    content = <Box textAlign="center" padding="l"><Spinner size="large" /></Box>;
  } else if (schemas.length === 0 && rules.length === 0) {
    content = (
      <Box textAlign="center" padding="l">
        <SpaceBetween size="m" alignItems="center">
          <Box>No data flow configured.</Box>
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setShowSchemaForm(true)} iconName="add-plus">Register schema</Button>
            <Button onClick={() => setShowRuleForm(true)} iconName="add-plus">Add rule</Button>
          </SpaceBetween>
        </SpaceBetween>
      </Box>
    );
  } else {
    content = (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isNarrow ? '1fr' : '1fr 280px',
          gap: 16,
        }}
      >
        <DataFlowGraph
          schemas={schemas}
          rules={rules}
          projectionConfig={projectionConfig}
          ingress={ingress}
          onNodeClick={handleNodeClick}
          highlightedNodeId={selectedId || undefined}
          onHandle={handleGraphHandle}
        />
        <div style={{ position: isNarrow ? 'static' : 'sticky', top: 0, alignSelf: 'start' }}>
          <GraphInfoPanel
            storeName={storeName}
            selectedNodeId={selectedId}
            schemas={schemaItems}
            rules={rules}
            ingress={ingress}
            projectionConfig={projectionConfig}
            onClose={handlePanelClose}
            onNavigate={handlePanelNavigate}
            onSelectNode={handlePanelSelectNode}
            onRegisterSchema={() => setShowSchemaForm(true)}
            onAddRule={() => setShowRuleForm(true)}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      {content}
      <Modal visible={showSchemaForm} size="large" onDismiss={() => setShowSchemaForm(false)} header="Register schema">
        <RegisterSchemaForm storeName={storeName} onDone={() => setShowSchemaForm(false)} />
      </Modal>
      <Modal visible={showRuleForm} size="large" onDismiss={() => setShowRuleForm(false)} header="Add routing rule">
        <AddRuleForm storeName={storeName} onDone={() => setShowRuleForm(false)} />
      </Modal>
    </>
  );
}
